/**
 * Shared API-error → user-message extraction.
 *
 * Narrows an unknown error to `ApiError`, then resolves a user-facing message:
 * backend error code via a per-domain code→i18n map, else flattened
 * validation `details`, else the raw `message`, else a domain fallback i18n key.
 * Used by the per-domain status-change helpers (tasks, obstructions).
 */
import i18n from '@/config/i18n';
import type { ApiError } from '@/types/api';

export function isApiError(error: unknown): error is ApiError {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as Record<string, unknown>;
  return (
    typeof candidate.code === 'string' && typeof candidate.message === 'string'
  );
}

export function extractApiErrorMessage(
  error: unknown,
  errorCodeI18n: Record<string, string>,
  fallbackI18nKey: string
): string {
  if (isApiError(error)) {
    if (error.backendErrorCode) {
      const i18nKey = errorCodeI18n[error.backendErrorCode];
      if (i18nKey) {
        return i18n.t(i18nKey);
      }
    }

    if (error.details) {
      const messages = Object.values(error.details).flat();
      if (messages.length > 0) {
        return messages.join('. ');
      }
    }

    return error.message;
  }

  return i18n.t(fallbackI18nKey);
}
